// src/hooks/useCustomerSearch.ts

import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import { fetchItems } from '../support/queries';
import { CacheKey } from '../types/cache-keys';

export interface CustomerSearchResult {
  id: string;
  name: string;
}

/**
 * Server-side search on Salesforce Account Name for parent selection.
 * Uses /api/sf/customer POST with search_string, same as the main customer table.
 */
export const useCustomerSearch = () => {
  const [term, setTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState(term);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedTerm(term.trim());
    }, 400);
    return () => clearTimeout(handle);
  }, [term]);

  const query = useQuery<CustomerSearchResult[]>({
    queryKey: [CacheKey.SalesforceCustomers, 'tree-parent-search', debouncedTerm],
    queryFn: async () => {
      if (!debouncedTerm) return [];
      const body = {
        filters: [],
        order_by: 'Name ASC',
        columns: ['Id', 'Name'],
        search_string: debouncedTerm,
      };
      const resp = await fetchItems(
        'sf/customer',
        { page: 1, size: 25 },
        { pagination: true, body },
      );
      const items = resp.items ?? resp.records ?? resp ?? [];
      return items.map((c: any) => ({
        id: c.Id ?? c.id,
        name: c.Name ?? c.name ?? (c.Id ?? c.id),
      }));
    },
    ...{
        enabled: !!debouncedTerm,
        placeholderData: keepPreviousData,
      },
  });

  return {
    term,
    setTerm,
    results: query.data ?? [],
    isLoading: query.isLoading,
  };
};